import { BASIC_TYPE, Logger, loggerManager } from '..';
import ErrorCode from '../common/error-code';
import { generateErrorMessage } from '../common/error-generator';
import { ErrorName } from '../common/error-info';
import RtcError from '../common/rtc-error';
import { isFunction } from '../utils';
import { validate } from '../utils/decorators/validate';
import TRTCClass from '../../../api-cloud-next/src/main';
import { TRTCEvent } from '../../../api-cloud-next/src/trtc-event';
import { TRTCStreamType } from '../../../api-cloud-next/src/type';
import { PlaybackQualitySwitcher, SwitchPlaybackQualityOptions } from './playback-quality-switcher';

export type ExperimentalAPIFunctionMap = {
  'enableAudioFrameEvent': EnableAudioFrameEventOptions;
  'resumeRemotePlayer': RemotePlayerOptions;
  'pauseRemotePlayer': RemotePlayerOptions;
  'requestPictureInPicture': RequestPictureInPictureOptions;
  'requestFullScreen': RequestFullScreenOptions;
  'switchPlaybackQuality': SwitchPlaybackQualityOptions;
  'preconnect': PreconnectParams;
}

export interface RequestPictureInPictureOptions { enable: boolean }
export interface RequestFullScreenOptions { enable: boolean }
export interface RemotePlayerOptions { userId: string, streamType?: TRTCStreamType }

export interface EnableAudioFrameEventOptions {
  enable: boolean
  userId: string
  sampleRate?: number
  channelCount?: number
  port?: MessagePort
}

class ExperimentalAPI {
  log: Logger;

  constructor() {
    this.log = loggerManager.createLogger({
      id: 'exp'
    });
  }

  async call<T extends keyof ExperimentalAPIFunctionMap>(name: T, options: { trtcInstance: TRTCClass} & ExperimentalAPIFunctionMap[T]) {
    if (isFunction(this[name])) {
      // @ts-ignore
      return this[name](options);
    }
    return Promise.reject(new RtcError({
      code: ErrorCode.INVALID_PARAMETER,
      message: generateErrorMessage({
        key: ErrorName.API_NOT_EXIST,
        data: { name }
      })
    }));
  }

  @validate({
    name: 'options',
    type: BASIC_TYPE.Object,
    required: true,
    properties: {
      enable: {
        required: true,
        type: BASIC_TYPE.Boolean
      },
      userId: {
        required: true,
        type: BASIC_TYPE.String
      },
      sampleRate: {
        type: BASIC_TYPE.Number,
        values: [8000, 16000, 32000, 44100, 48000]
      },
      channelCount: {
        type: BASIC_TYPE.Number,
        values: [1, 2]
      },
      port: {
        type: 'messageport'
      }
    }
  })
  async enableAudioFrameEvent(options: { trtcInstance: TRTCClass } & EnableAudioFrameEventOptions) {
    const { trtcInstance, enable, userId, sampleRate = 48000, channelCount = 1, port } = options;
    const { audioManager } = trtcInstance.room;
    const { getPCMAbortCtrlMap, audioFrameEventConfigMap } = audioManager;
    audioFrameEventConfigMap.set(userId, { enable, sampleRate, channelCount, port });
    if (enable) {
      if (userId === '*') {
        trtcInstance.room.remotePublishedUserMap.forEach(remotePublishedUser => {
          if (remotePublishedUser.remoteAudioTrack.isAvailable) {
            if (getPCMAbortCtrlMap.get(remotePublishedUser.userId)) return;
            const abortCtrl = audioManager.getPCM(datas => {
              trtcInstance.emit(TRTCEvent.AUDIO_FRAME, datas);
            }, remotePublishedUser.userId);
            getPCMAbortCtrlMap.set(remotePublishedUser.userId, abortCtrl);
          }
        });
      } else {
        if (getPCMAbortCtrlMap.get(userId)) return;
        const abortCtrl = audioManager.getPCM(datas => {
          trtcInstance.emit(TRTCEvent.AUDIO_FRAME, datas);
        }, userId);
        getPCMAbortCtrlMap.set(userId, abortCtrl);
      }
    } else {
      if (userId === '*') {
        trtcInstance.room.remotePublishedUserMap.forEach(remotePublishedUser => {
          if (remotePublishedUser.remoteAudioTrack.isSubscribed) {
            const { userId } = remotePublishedUser;
            const abortCtrl = getPCMAbortCtrlMap.get(userId);
            abortCtrl?.abort('disable');
            getPCMAbortCtrlMap.delete(userId);
          }
        });
      } else {
        const abortCtrl = getPCMAbortCtrlMap.get(userId);
        abortCtrl?.abort('disable');
        getPCMAbortCtrlMap.delete(userId);
      }
    }
  }

  async resumeRemotePlayer(options: { trtcInstance: TRTCClass } & RemotePlayerOptions) {
    if (options.userId === '*') {
      const promiseList: Promise<void>[] = [];
      options.trtcInstance.room.remotePublishedUserMap.forEach(remotePublishedUser => {
        const { remoteAudioTrack, remoteVideoTrack, remoteAuxiliaryTrack } = remotePublishedUser;
        if (!options.streamType) {
          if (remoteAudioTrack.isAvailable) promiseList.push(remoteAudioTrack.player.resume());
          if (remoteVideoTrack.isAvailable) promiseList.push(remoteVideoTrack.player.resume());
          if (remoteAuxiliaryTrack.isAvailable) promiseList.push(remoteAuxiliaryTrack.player.resume());
        } else if (options.streamType === TRTCStreamType.Main) {
          if (remoteAudioTrack.isAvailable) promiseList.push(remoteAudioTrack.player.resume());
          if (remoteVideoTrack.isAvailable) promiseList.push(remoteVideoTrack.player.resume());
        } else {
          if (remoteAuxiliaryTrack.isAvailable) promiseList.push(remoteAuxiliaryTrack.player.resume());
        }
      });
      return Promise.all(promiseList);
    }
    const remotePublisherUser = options.trtcInstance.room.remotePublishedUserMap.get(options.userId);
    if (remotePublisherUser) {
      if (options.streamType === TRTCStreamType.Main) {
        return Promise.all([remotePublisherUser.remoteAudioTrack.player.resume(), remotePublisherUser.remoteVideoTrack.player.resume()]);
      }
      return remotePublisherUser.remoteAuxiliaryTrack.player.resume();
    }
  }
  pauseRemotePlayer(options: { trtcInstance: TRTCClass } & RemotePlayerOptions) {
    if (options.userId === '*') {
      options.trtcInstance.room.remotePublishedUserMap.forEach(remotePublishedUser => {
        const { remoteAudioTrack, remoteVideoTrack, remoteAuxiliaryTrack } = remotePublishedUser;
        if (!options.streamType) {
          if (remoteAudioTrack.isAvailable) remoteAudioTrack.player.pause();
          if (remoteVideoTrack.isAvailable) remoteVideoTrack.player.pause(false);
          if (remoteAuxiliaryTrack.isAvailable) remoteAuxiliaryTrack.player.pause(false);
        } else if (options.streamType === TRTCStreamType.Main) {
          if (remoteAudioTrack.isAvailable) remoteAudioTrack.player.pause();
          if (remoteVideoTrack.isAvailable) remoteVideoTrack.player.pause(false);
        } else {
          if (remoteAuxiliaryTrack.isAvailable) remoteAuxiliaryTrack.player.pause(false);
        }
      });
    } else {
      const remotePublisherUser = options.trtcInstance.room.remotePublishedUserMap.get(options.userId);
      if (remotePublisherUser) {
        if (options.streamType === TRTCStreamType.Main) {
          remotePublisherUser.remoteAudioTrack.player.pause();
          remotePublisherUser.remoteVideoTrack.player.pause(false);
        } else {
          remotePublisherUser.remoteAuxiliaryTrack.player.pause(false);
        }
      }
    }
  }

  requestPictureInPicture(options: { trtcInstance: TRTCClass } & RequestPictureInPictureOptions) {
    const theFirstRemoteVideoAvailableUser = [...options.trtcInstance.room.remotePublishedUserMap.values()].find(remotePublishedUser => remotePublishedUser.remoteVideoTrack.isAvailable);
    if (!theFirstRemoteVideoAvailableUser) return Promise.reject(new RtcError({
      code: ErrorCode.INVALID_OPERATION,
      message: 'no available remote video'
    }));
    if (options.enable) {
      return theFirstRemoteVideoAvailableUser.remoteVideoTrack.player.enterPictureInPicture();
    }
    return theFirstRemoteVideoAvailableUser.remoteVideoTrack.player.exitPictureInPicture();
  }

  requestFullScreen(options: { trtcInstance: TRTCClass } & RequestFullScreenOptions) {
    const theFirstRemoteVideoAvailableUser = [...options.trtcInstance.room.remotePublishedUserMap.values()].find(remotePublishedUser => remotePublishedUser.remoteVideoTrack.isAvailable);
    if (!theFirstRemoteVideoAvailableUser) return Promise.reject(new RtcError({
      code: ErrorCode.INVALID_OPERATION,
      message: 'no available remote video'
    }));
    if (options.enable) {
      return theFirstRemoteVideoAvailableUser.remoteVideoTrack.player.enterFullscreen();
    }
    return theFirstRemoteVideoAvailableUser.remoteVideoTrack.player.exitFullscreen();
  }

  async switchPlaybackQuality(options: { trtcInstance: TRTCClass } & SwitchPlaybackQualityOptions) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const instance = options.trtcInstance as any;
    // 懒加载：第一次调用时创建，挂载到 trtcInstance 上
    if (!instance._playbackQualitySwitcher) {
      instance._playbackQualitySwitcher = new PlaybackQualitySwitcher();
    }
    return instance._playbackQualitySwitcher.switchPlaybackQuality(options);
  }

  @validate({
    name: 'options',
    type: BASIC_TYPE.Object,
    required: true,
    properties: {
      enable: {
        required: true,
        type: BASIC_TYPE.Boolean
      },
      userId: {
        required: false,
        type: BASIC_TYPE.String
      },
      sdkAppId: {
        required: false,
        type: BASIC_TYPE.Number
      },
      userSig: {
        required: false,
        type: BASIC_TYPE.String
      },
      roomId: {
        required: false,
        type: BASIC_TYPE.Number
      },
      strRoomId: {
        required: false,
        type: BASIC_TYPE.String
      }
    }
  })
  async preconnect(options: { trtcInstance: TRTCClass } & PreconnectParams) {
    const { trtcInstance } = options;
    if (options.enable) {
      return trtcInstance.room.preconnect(options.sdkAppId, options.userId, options.userSig, TRTCClass.frameWorkType, options.roomId, options.strRoomId);
    }
    return trtcInstance.room.closePreconnect();
  }
}

export default new ExperimentalAPI();
